/*
 * This file is part of the Web3 Library developed by mFactory GmbH.
 *
 * Copyright © 2021, mFactory GmbH
 *
 * Solana Reference Stake Pool is free software: you can redistribute it
 * and/or modify it under the terms of the GNU Affero General Public License
 * as published by the Free Software Foundation, either version 3
 * of the License, or (at your option) any later version.
 *
 * Solana Reference Stake Pool is distributed in the hope that it
 * will be useful, but WITHOUT ANY WARRANTY; without even the implied
 * warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.
 * If not, see <https://www.gnu.org/licenses/agpl-3.0.html>.
 *
 * You can be released from the requirements of the Affero GNU General Public License
 * by purchasing a commercial license. The purchase of such a license is
 * mandatory as soon as you develop commercial activities using the
 * Solana Reference Stake Pool code without disclosing the source code of
 * your own applications.
 *
 * The developer of this program can be contacted at <info@mfactory.ch>.
 */

import { lamportsToSol } from './web3'

const SI_SYMBOL = ['', 'K', 'M', 'G', 'T', 'P', 'E']

export function getSolPriceFormatter(n = 5) {
  return new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: n,
    maximumFractionDigits: n,
  })
}

export const priceFormatter = new Intl.NumberFormat('en-US', {
  style: 'decimal',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

export const longPriceFormatter = new Intl.NumberFormat('en-US', {
  style: 'decimal',
  minimumFractionDigits: 2,
  maximumFractionDigits: 5,
})

export const feeFormatter = new Intl.NumberFormat('en-US', {
  style: 'decimal',
  minimumFractionDigits: 2,
  maximumFractionDigits: 9,
})

export const formatPct = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

function abbreviateNumber(number: number, precision: number, trimZeros = false) {
  const tier = (Math.log10(number) / 3) | 0
  let scaled = number
  const suffix = SI_SYMBOL[tier]
  if (tier > 0) {
    const scale = 10 ** (tier * 3)
    scaled = number / scale
  }

  if (isNaN(scaled)) {
    return ''
  }
  const result = trimZeros ? Number(scaled.toFixed(precision)) : scaled.toFixed(precision)
  return result + (typeof suffix === 'string' ? suffix : '')
}

export function formatAmount(val: number, precision = 5, abbr = true) {
  return abbr ? abbreviateNumber(val, precision) : val.toFixed(precision)
}

export function formatAndTrimAmount(val: number, precision = 5, abbr = true) {
  return abbr ? abbreviateNumber(val, precision, true) : Number(val.toFixed(precision))
}

export function formatAmountPrice(val: number | bigint) {
  return priceFormatter.format(val)
}

export function formatMoney(val: string | number): string {
  if (typeof val === 'undefined' || val === null || val === '') {
    return ''
  }

  val = val.toString()

  const decimalSeparator = val.lastIndexOf('.')

  let integerPart = decimalSeparator >= 0 ? val.slice(0, decimalSeparator) : val
  let fractionalPart = decimalSeparator >= 0 ? val.slice(decimalSeparator + 1) : null

  if (fractionalPart) {
    fractionalPart = fractionalPart.slice(0, 2).replace(/\D+/g, '')
    if (fractionalPart.length === 1) {
      fractionalPart += '0'
    }
  } else if (!fractionalPart) {
    fractionalPart = '00'
  }

  integerPart = integerPart.replace(/\D+/g, '')
  if (!integerPart) {
    integerPart = '0'
  }

  let formatted = ''
  for (let i = 0; i < integerPart.length; i++) {
    if (i !== 0 && i % 3 === 0) {
      formatted = `${integerPart[integerPart.length - i - 1]},${formatted}`
    } else {
      formatted = integerPart[integerPart.length - i - 1] + formatted
    }
  }

  formatted += `.${fractionalPart}`

  return formatted
}

export function isInt(n: string | number): boolean {
  return Number(n) === n && n % 1 === 0
}

export function isInvalidFloat(n: string | number): boolean {
  return isNaN(Number(n))
}

export function isInvalidTime(n: string, p: string): boolean {
  const num = Number(n)
  if (!isInt(num) || isInvalidFloat(num)) {
    return true
  }
  let max = 12
  if (p.toLocaleLowerCase() === 'year') {
    max = 20
  } else if (p.toLocaleLowerCase() === 'epoch') {
    max = 365
  }

  return num < 1 || num > max
}

export function solFormatter(val: number, text = ' SOL') {
  if (val === 0) {
    return `0${text}`
  }
  const decimals = Math.max(13 - `${val}`.length, 5)
  const solPriceFormatter = getSolPriceFormatter(decimals)
  return `${val < 0 ? '-' : ''}${solPriceFormatter.format(lamportsToSol(val))}${text}`
}
